import { useMemo } from 'react';
import useSPGroups from '@/hooks/swr/group/useSPGroups';
import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';

//libs
import {
    nbItemsDisplayedNoActiveItem,
    nbItemsDisplayed
} from '@/lib/menu/RightMenu/itemsDisplayed';

//utils
import { updateActiveItem } from '@/utils/menu/updateActiveItem';
import { getMenuItemsOrder } from '@/utils/menu/getMenuItemsOrder';
import useActiveGroup from '@/hooks/user/useActiveGroup';
import { useGlobalInfo, TOGGLE_MENU_SELECTED_GROUP_OPEN } from '@/hooks/context/useGlobalInfo';

//components
import { EmptyGroupsMenu } from '@/components/composite/SocialProfiles/SocialProfileGroupMenu/Components/MenuContent/Groups/EmptyGroupsMenu';
import { GroupsLoader } from '@/components/composite/SocialProfiles/SocialProfileGroupMenu/Components/MenuContent/Groups/GroupsLoader';
import { ListWrapper } from '@/components/composite/SocialProfiles/Components/ListWrapper';
import { AvatarMenu } from '@/components/composite/SocialProfiles/Components/AvatarMenu';
import { RightMenuPopover } from '@/components/composite/SocialProfiles/SocialProfileGroupMenu/Components/RightMenuPopover';

type MenuGroupContentProps = {
    groups: SpGroupType[];
};
function MenuGroupsContent({ groups }: MenuGroupContentProps) {
    const { activeGroup, setActiveGroup } = useActiveGroup();
    const { dispatch } = useGlobalInfo();
    const nbGroups = groups.length;
    const nbAdditionalGroups = nbGroups - nbItemsDisplayedNoActiveItem;

    const orderedItems = useMemo(() => {
        return getMenuItemsOrder<SpGroupType>({
            storageKey: 'ico-group-order',
            menuItems: groups,
            activeItemId: activeGroup?.id ?? null
        });
    }, [activeGroup?.id, groups]);

    const updateActive = (item: SpGroupType) => {
        updateActiveItem<SpGroupType>({
            storageKey: 'ico-group-order',
            itemsList: groups,
            itemSelectedId: item?.id,
            callback: () => {
                dispatch({ type: TOGGLE_MENU_SELECTED_GROUP_OPEN, payload: true });
                setActiveGroup(item);
                const activeElement = document.activeElement as HTMLButtonElement;
                activeElement.blur();
            }
        });
    };

    return (
        <ListWrapper data-testid="menu-groups">
            {activeGroup ? (
                <AvatarMenu
                    data-testid="active-group-element"
                    hasHover
                    variant="group"
                    key={activeGroup.id}
                    data={activeGroup}
                    selected
                    onClick={() => {
                        updateActive(activeGroup);
                    }}
                />
            ) : null}
            {orderedItems.map((item, index) => {
                if (index < (activeGroup ? nbItemsDisplayed : nbItemsDisplayedNoActiveItem)) {
                    return (
                        <AvatarMenu
                            data-testid="visible-groupe"
                            hasHover
                            variant="group"
                            key={item.id}
                            data={item}
                            onClick={() => {
                                updateActive(item);
                            }}
                        />
                    );
                }
                return null;
            })}
            {nbAdditionalGroups > 0 ? (
                <RightMenuPopover
                    nbItems={nbAdditionalGroups}
                    activeItem={activeGroup}
                    itemsList={orderedItems}
                    updateActiveItem={updateActive}
                    variant="group"
                />
            ) : null}
        </ListWrapper>
    );
}

export function GroupsMenu() {
    const { spGroups, isLoading } = useSPGroups({
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    if (isLoading) {
        return <GroupsLoader />;
    }

    if (!spGroups.length) {
        return <EmptyGroupsMenu />;
    }

    return <MenuGroupsContent groups={spGroups.filter((group) => group.active)} />;
}
